import { faStar } from '@fortawesome/free-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FC, UIEvent, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { TruncatedTooltip } from 'src/components/TruncatedTooltip'
import { cn } from 'src/utils/cn'
import { ReviewComparisonFeedRow } from 'src/services/reviewAnalysisApi/interfaces/reviewComparison.interface'
import styles from './sentimentFeed.module.scss'
import { facilityColor } from '../palette'

interface SentimentFeedProps {
  facilities: string[]
  rows: ReviewComparisonFeedRow[]
}

const COPY = {
  subtitle:
    'Latest reviews from both facilities in one combined, filterable stream',
  title: 'Recent Sentiment Feed',
}

const COLUMNS = [
  'Facility',
  'Source',
  'Location',
  'Rating',
  'Sentiment',
  'Posted',
]

// The tab that shows the stream unfiltered, rather than a sentiment of its own.
const ALL_TAB = 'all'

const FILTERS: { id: string; label: string }[] = [
  { id: ALL_TAB, label: 'All' },
  { id: 'positive', label: 'Positive' },
  { id: 'negative', label: 'Negative' },
  { id: 'neutral', label: 'Neutral' },
]

const SENTIMENT_CLASS: Record<string, string> = {
  negative: styles.negative,
  neutral: styles.neutral,
  positive: styles.positive,
}

const SENTIMENT_LABEL: Record<string, string> = {
  negative: 'Negative',
  neutral: 'Neutral',
  positive: 'Positive',
}

// "19h ago", from the date the API sends. Written here rather than sent by the
// server because it is a rendering of the timestamp, and it goes stale on a
// page left open — the server's answer would be stale the moment it arrived.
const postedAgo = (iso: string) => {
  const minutes = Math.max(
    0,
    Math.round((Date.now() - new Date(iso).getTime()) / 60000)
  )

  if (minutes < 60) return `${minutes}m ago`
  if (minutes < 60 * 24) return `${Math.round(minutes / 60)}h ago`

  return `${Math.round(minutes / (60 * 24))}d ago`
}

// How close to the bottom counts as the end of the list. A couple of pixels of
// slack, because fractional scroll heights never land exactly on zero.
const SCROLL_END_SLACK = 2

export const SentimentFeed: FC<SentimentFeedProps> = ({ facilities, rows }) => {
  const [filter, setFilter] = useState<string>(ALL_TAB)
  const [isAtEnd, setIsAtEnd] = useState(false)
  const bodyRef = useRef<HTMLDivElement>(null)

  const visible = useMemo(
    () =>
      filter === ALL_TAB
        ? rows
        : rows.filter((row) => row.sentiment === filter),
    [filter, rows]
  )

  const onScroll = (event: UIEvent<HTMLDivElement>) => {
    const { clientHeight, scrollHeight, scrollTop } = event.currentTarget
    setIsAtEnd(scrollHeight - scrollTop - clientHeight <= SCROLL_END_SLACK)
  }

  // A filter that leaves fewer rows than the list can hold has nothing below
  // the fold, so the fade has to lift even though nobody scrolled.
  useLayoutEffect(() => {
    const body = bodyRef.current
    if (!body) return
    setIsAtEnd(body.scrollHeight - body.clientHeight <= SCROLL_END_SLACK)
  }, [visible])

  return (
    <section>
      <div className={styles.header}>
        <div className={styles.headerText}>
          <h3 className={styles.title}>{COPY.title}</h3>
          <p className={styles.subtitle}>{COPY.subtitle}</p>
        </div>

        <div className={styles.filters}>
          {FILTERS.map((option) => (
            <button
              className={cn(
                styles.filter,
                filter === option.id && styles.filterActive
              )}
              key={option.id}
              onClick={() => setFilter(option.id)}
              type="button"
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.headRow}>
        {COLUMNS.map((column) => (
          <span className={styles.headCell} key={column}>
            {column}
          </span>
        ))}
      </div>

      {/* The last row fades out to say the list carries on past the fold, and
          stops fading once there is nothing below it — a permanent fade would
          leave the final review unreadable. */}
      <div
        className={cn(styles.body, !isAtEnd && styles.bodyFaded)}
        onScroll={onScroll}
        ref={bodyRef}
      >
        {visible.map((row, index) => (
          <div
            className={styles.row}
            key={`${row.source}-${row.date}-${index}`}
          >
            <span className={styles.cell}>
              <span
                className={styles.facilityDot}
                style={{
                  background: facilityColor(facilities.indexOf(row.facilityId)),
                }}
              />
              <TruncatedTooltip text={row.facilityName} />
            </span>

            <span className={styles.cell}>{row.source}</span>
            <span className={styles.cell}>{row.location}</span>

            <span className={styles.cell}>
              {row.rating.toFixed(1)}
              <FontAwesomeIcon className={styles.star} icon={faStar} />
            </span>

            <span className={styles.cell}>
              <span className={cn(styles.pill, SENTIMENT_CLASS[row.sentiment])}>
                {SENTIMENT_LABEL[row.sentiment]}
              </span>
            </span>

            <span className={cn(styles.cell, styles.posted)}>
              {postedAgo(row.date)}
            </span>
          </div>
        ))}
      </div>
    </section>
  )
}
